import type { SimulationGraph } from '../models/simulation';
import ELK from 'elkjs/lib/elk.bundled.js';

/**
 * Use the open-source ELK layered algorithm to generate tidy coordinates for the
 * current simulation graph. ELK handles crossing minimisation, spacing, and
 * hierarchical ordering so we avoid reinventing layout math.
 */

const elk = new ELK();
const COLUMN_SNAP_TOLERANCE = 48;
const DEFAULT_COLUMN_GAP = 110;
const VALUE_CHAIN_COLUMN_GAP = 260;
const VALUE_CHAIN_ROW_GAP = 80;
const VALUE_CHAIN_LANE_GAP = 180;

export const DEFAULT_NODE_DIMENSIONS = {
    // Match the compact rendered SimulationNodeCard footprint.
    width: 300,
    height: 240
};

const DEFAULT_LAYOUT_OPTIONS = {
    'elk.algorithm': 'layered',
    'elk.direction': 'RIGHT',
    'elk.layered.spacing.nodeNodeBetweenLayers': '120',
    'elk.spacing.nodeNode': '90',
    'elk.spacing.edgeNode': '60',
    'elk.spacing.edgeEdge': '30',
    'elk.layered.spacing.edgeNodeBetweenLayers': '70',
    'elk.layered.spacing.edgeEdgeBetweenLayers': '40',
    'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
    'elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED',
    'elk.layered.compaction.quality': 'HIGH',
    'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
    'elk.layered.mergeEdges': 'false'
} as const;

type NodePosition = { x: number; y: number };
type GraphNode = SimulationGraph['nodes'][number];
type LaneId =
    | 'scale'
    | 'inputs'
    | 'operations'
    | 'logistics'
    | 'products'
    | 'costs'
    | 'profit'
    | `topology:${string}`;

const VALUE_CHAIN_LANE_ORDER: LaneId[] = [
    'scale',
    'inputs',
    'operations',
    'logistics',
    'products',
    'costs',
    'profit'
];

const normalizeLayoutText = (value: string): string =>
    value
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();

const hasAnyToken = (text: string, tokens: string[]): boolean =>
    tokens.some((token) => text.includes(token));

const inferSemanticLane = (node: GraphNode): Exclude<LaneId, `topology:${string}`> | undefined => {
    const tags = node.data.tags?.map(normalizeLayoutText).filter(Boolean) ?? [];
    const tagText = tags.join(' ');
    const nameText = normalizeLayoutText(node.data.name);
    const text = tagText || nameText;

    if (hasAnyToken(text, ['profit', 'margin', 'ebit', 'income', 'cash flow', 'net result', 'output'])) {
        return 'profit';
    }
    if (hasAnyToken(text, ['transport', 'truck', 'freight', 'shipping', 'logistic', 'distance', 'storage'])) {
        return 'logistics';
    }
    if (hasAnyToken(text, ['cost', 'expense', 'opex', 'capex', 'payment', 'fee', 'price paid'])) {
        return 'costs';
    }
    if (hasAnyToken(text, ['revenue', 'sales', 'price', 'product', 'oil', 'meal', 'yield value'])) {
        return 'products';
    }
    if (hasAnyToken(text, [
        'operation',
        'process',
        'processing',
        'production',
        'cultivation',
        'harvest',
        'sowing',
        'seeding',
        'crush',
        'clean',
        'dry',
        'extract',
        'recover',
        'convert'
    ])) {
        return 'operations';
    }
    if (hasAnyToken(text, ['seed', 'fertilizer', 'nitrogen', 'phosphorus', 'potassium', 'herbicide', 'material', 'input'])) {
        return 'inputs';
    }
    if (hasAnyToken(text, ['scale', 'farm', 'hectare', 'area', 'capacity', 'rate', 'ratio', 'efficiency', 'content', 'allowance'])) {
        return 'scale';
    }

    if (node.data.isOutput) {
        return 'profit';
    }

    return undefined;
};

const sortNodesByName = (a: GraphNode, b: GraphNode) =>
    a.data.name.localeCompare(b.data.name, undefined, { numeric: true, sensitivity: 'base' });

const buildValidEdgeMaps = (graph: SimulationGraph) => {
    const nodeIds = new Set(graph.nodes.map((node) => node.id));
    const inbound = new Map<string, string[]>();
    const outbound = new Map<string, string[]>();

    graph.nodes.forEach((node) => {
        inbound.set(node.id, []);
        outbound.set(node.id, []);
    });

    graph.edges.forEach((edge) => {
        if (!nodeIds.has(edge.source) || !nodeIds.has(edge.target)) {
            return;
        }
        outbound.get(edge.source)?.push(edge.target);
        inbound.get(edge.target)?.push(edge.source);
    });

    return { inbound, outbound };
};

const computeTopologicalDepths = (
    graph: SimulationGraph,
    inbound: Map<string, string[]>,
    outbound: Map<string, string[]>
) => {
    const nodeById = new Map(graph.nodes.map((node) => [node.id, node]));
    const indegree = new Map(graph.nodes.map((node) => [node.id, inbound.get(node.id)?.length ?? 0]));
    const depths = new Map(graph.nodes.map((node) => [node.id, 0]));
    const order: GraphNode[] = [];
    const queue = graph.nodes
        .filter((node) => (indegree.get(node.id) ?? 0) === 0)
        .sort(sortNodesByName);

    while (queue.length > 0) {
        const node = queue.shift()!;
        order.push(node);

        (outbound.get(node.id) ?? []).forEach((targetId) => {
            depths.set(targetId, Math.max(depths.get(targetId) ?? 0, (depths.get(node.id) ?? 0) + 1));
            indegree.set(targetId, (indegree.get(targetId) ?? 0) - 1);

            if (indegree.get(targetId) === 0) {
                const targetNode = nodeById.get(targetId);
                if (targetNode) {
                    queue.push(targetNode);
                    queue.sort(sortNodesByName);
                }
            }
        });
    }

    if (order.length !== graph.nodes.length) {
        throw new Error('Graph contains cycles; unable to calculate value-chain layout.');
    }

    return { depths, order };
};

const nearestDownstreamSinkName = (
    nodeId: string,
    nodeById: Map<string, GraphNode>,
    outbound: Map<string, string[]>
): string => {
    const visited = new Set<string>([nodeId]);
    const queue = [...(outbound.get(nodeId) ?? [])].sort((a, b) =>
        (nodeById.get(a)?.data.name ?? a).localeCompare(nodeById.get(b)?.data.name ?? b)
    );

    while (queue.length > 0) {
        const currentId = queue.shift()!;
        if (visited.has(currentId)) {
            continue;
        }
        visited.add(currentId);

        const node = nodeById.get(currentId);
        const children = outbound.get(currentId) ?? [];
        if (node && (children.length === 0 || node.data.isOutput)) {
            return normalizeLayoutText(node.data.name) || currentId;
        }

        queue.push(
            ...children
                .filter((childId) => !visited.has(childId))
                .sort((a, b) => (nodeById.get(a)?.data.name ?? a).localeCompare(nodeById.get(b)?.data.name ?? b))
        );
    }

    return normalizeLayoutText(nodeById.get(nodeId)?.data.name ?? nodeId) || nodeId;
};

const inferLayoutLanes = (
    graph: SimulationGraph,
    outbound: Map<string, string[]>
): Map<string, LaneId> => {
    const nodeById = new Map(graph.nodes.map((node) => [node.id, node]));
    const lanes = new Map<string, LaneId>();

    graph.nodes.forEach((node) => {
        const semanticLane = inferSemanticLane(node);
        if (semanticLane) {
            lanes.set(node.id, semanticLane);
            return;
        }

        lanes.set(node.id, `topology:${nearestDownstreamSinkName(node.id, nodeById, outbound)}`);
    });

    return lanes;
};

const compareLaneIds = (a: LaneId, b: LaneId): number => {
    const aIndex = VALUE_CHAIN_LANE_ORDER.indexOf(a);
    const bIndex = VALUE_CHAIN_LANE_ORDER.indexOf(b);

    if (aIndex >= 0 && bIndex >= 0) {
        return aIndex - bIndex;
    }
    if (aIndex >= 0) {
        return -1;
    }
    if (bIndex >= 0) {
        return 1;
    }

    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
};

const deriveColumnsFromX = (
    positions: Record<string, NodePosition>,
    tolerance = COLUMN_SNAP_TOLERANCE
): Record<string, number> => {
    const sorted = Object.entries(positions)
        .map(([id, position]) => ({ id, x: position.x, y: position.y }))
        .sort((a, b) => {
            if (a.x === b.x) {
                return a.y - b.y;
            }
            return a.x - b.x;
        });

    if (sorted.length === 0) {
        return {};
    }

    const columns: Record<string, number> = {};
    let currentColumn = 0;
    let anchorX = sorted[0].x;

    sorted.forEach((node) => {
        if (node.x - anchorX > tolerance) {
            currentColumn += 1;
            anchorX = node.x;
        }
        columns[node.id] = currentColumn;
    });

    return columns;
};

/**
 * Snap ELK x-coordinates to explicit columns so each dependency layer is visibly
 * distinct from left to right.
 */
export const snapPositionsToColumns = (
    positions: Record<string, NodePosition>,
    width = DEFAULT_NODE_DIMENSIONS.width,
    gap = DEFAULT_COLUMN_GAP
): Record<string, NodePosition> => {
    const entries = Object.entries(positions);
    if (entries.length === 0) {
        return {};
    }

    const columns = deriveColumnsFromX(positions);
    const minX = Math.min(...entries.map(([, position]) => position.x));
    const columnSpacing = width + gap;

    const snapped: Record<string, NodePosition> = {};
    entries.forEach(([id, position]) => {
        const columnIndex = columns[id] ?? 0;
        snapped[id] = {
            x: minX + columnIndex * columnSpacing,
            y: position.y
        };
    });

    return snapped;
};

export const layoutGraphWithValueChain = (
    graph: SimulationGraph,
    width = DEFAULT_NODE_DIMENSIONS.width,
    height = DEFAULT_NODE_DIMENSIONS.height,
    columnGap = VALUE_CHAIN_COLUMN_GAP,
    rowGap = VALUE_CHAIN_ROW_GAP,
    laneGap = VALUE_CHAIN_LANE_GAP
): Record<string, NodePosition> => {
    if (graph.nodes.length === 0) {
        return {};
    }

    const { inbound, outbound } = buildValidEdgeMaps(graph);
    const { depths, order } = computeTopologicalDepths(graph, inbound, outbound);
    const maxDepth = Math.max(...graph.nodes.map((node) => depths.get(node.id) ?? 0));
    const stages = new Map<string, number>();

    graph.nodes.forEach((node) => {
        const parents = inbound.get(node.id) ?? [];
        const children = outbound.get(node.id) ?? [];
        let stage = depths.get(node.id) ?? 0;

        if (parents.length === 0 && children.length > 0) {
            const firstConsumerStage = Math.min(...children.map((childId) => depths.get(childId) ?? stage + 1));
            stage = Math.max(0, firstConsumerStage - 1);
        }

        if (children.length === 0 || node.data.isOutput) {
            stage = Math.max(stage, maxDepth);
        }

        stages.set(node.id, stage);
    });

    order.forEach((node) => {
        const sourceStage = stages.get(node.id) ?? 0;
        (outbound.get(node.id) ?? []).forEach((targetId) => {
            stages.set(targetId, Math.max(stages.get(targetId) ?? 0, sourceStage + 1));
        });
    });

    const lanesByNodeId = inferLayoutLanes(graph, outbound);
    const nodesByLane = new Map<LaneId, GraphNode[]>();
    graph.nodes.forEach((node) => {
        const lane = lanesByNodeId.get(node.id) ?? 'operations';
        const laneNodes = nodesByLane.get(lane) ?? [];
        laneNodes.push(node);
        nodesByLane.set(lane, laneNodes);
    });

    const lanes = Array.from(nodesByLane.keys()).sort(compareLaneIds);
    const columnSpacing = width + columnGap;
    const rowSpacing = height + rowGap;
    const positions: Record<string, NodePosition> = {};
    let laneTop = 0;

    lanes.forEach((lane) => {
        const laneNodes = nodesByLane.get(lane) ?? [];
        const nodesByStage = new Map<number, GraphNode[]>();

        laneNodes.forEach((node) => {
            const stage = stages.get(node.id) ?? 0;
            const stageNodes = nodesByStage.get(stage) ?? [];
            stageNodes.push(node);
            nodesByStage.set(stage, stageNodes);
        });

        let maxRowsInLane = 1;
        nodesByStage.forEach((stageNodes) => {
            stageNodes.sort((a, b) => {
                const depthDiff = (depths.get(a.id) ?? 0) - (depths.get(b.id) ?? 0);
                if (depthDiff !== 0) {
                    return depthDiff;
                }
                return sortNodesByName(a, b);
            });
            maxRowsInLane = Math.max(maxRowsInLane, stageNodes.length);
        });

        nodesByStage.forEach((stageNodes, stage) => {
            stageNodes.forEach((node, rowIndex) => {
                positions[node.id] = {
                    x: stage * columnSpacing,
                    y: laneTop + rowIndex * rowSpacing
                };
            });
        });

        laneTop += maxRowsInLane * rowSpacing + laneGap;
    });

    return positions;
};

export const layoutGraphWithElk = async (
    graph: SimulationGraph,
    width = DEFAULT_NODE_DIMENSIONS.width,
    height = DEFAULT_NODE_DIMENSIONS.height
): Promise<Record<string, { x: number; y: number }>> => {
    if (graph.nodes.length === 0) {
        return {};
    }

    const elkGraph = {
        id: graph.id,
        layoutOptions: { ...DEFAULT_LAYOUT_OPTIONS } as Record<string, string>,
        children: graph.nodes.map((node) => ({
            id: node.id,
            width,
            height
        })),
        edges: graph.edges.map((edge) => ({
            id: edge.id,
            sources: [edge.source],
            targets: [edge.target]
        }))
    };

    const { children } = await elk.layout(elkGraph);
    const positions: Record<string, { x: number; y: number }> = {};

    children?.forEach((child) => {
        const x = (child.x ?? 0) - width / 2;
        const y = (child.y ?? 0) - height / 2;
        positions[child.id] = { x, y };
    });

    return snapPositionsToColumns(positions, width);
};

export const layoutGraphWithDefault = async (
    graph: SimulationGraph,
    width = DEFAULT_NODE_DIMENSIONS.width,
    height = DEFAULT_NODE_DIMENSIONS.height
): Promise<Record<string, NodePosition>> => {
    try {
        return layoutGraphWithValueChain(graph, width, height);
    } catch (_error) {
        return layoutGraphWithElk(graph, width, height);
    }
};
